import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton } from "@mui/material";
import * as XLSX from "xlsx";
import axios from "../../utils/api.js";

const ScanningcenterDetails = () => {
  const [scanningcenters, setScanningcenter] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchScanningcenter = async () => {
      const token = localStorage.getItem("token");
      try {
        const response = await axios.get("/get-scanning-center", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setScanningcenter(response.data);
      } catch (error) {
        console.error("Error fetching scanningcenter:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchScanningcenter();
  }, []);

  // Function to auto-size columns based on the longest data entry
  const autoFitColumns = (jsonData) => {
    return Object.keys(jsonData[0] || {}).map((key) => ({
      wch:
        Math.max(
          key.length, // Minimum width should fit the header text
          ...jsonData.map((row) => (row[key] ? row[key].toString().length : 0)) // Longest cell
        ) + 5, // Add some extra padding
    }));
  };

  // Function to download Excel
  const handleDownloadExcel = () => {
    if (scanningcenters.length === 0) {
      alert("No data available to download.");
      return;
    }
    const centerSheet = scanningcenters.map((scanningcenter, index) => ({
      "S.No": index + 1,
      "Center Name": scanningcenter.centerInfo?.centerName || "N/A",
      "Center Type": scanningcenter.centerInfo?.centerType || "N/A",
      Accreditations:
        scanningcenter.centerInfo?.accreditations?.join(", ") || "N/A",
      "Total Floor Area (sq. ft.)":
        scanningcenter.centerInfo?.totalFloorArea || "N/A",
      "Total Number of Rooms":
        scanningcenter.centerInfo?.totalNumberOfRooms || "N/A",
    }));

    const dataSheet = scanningcenters.map((scanningcenter, index) => ({
      "S.No": index + 1,
      "Center Name": scanningcenter.centerInfo?.centerName || "N/A",
      Date: scanningcenter.dataCollection?.date
        ? new Date(scanningcenter.dataCollection.date).toLocaleDateString()
        : "N/A",
      "Collected By":
        scanningcenter.dataCollection?.collectedBy?.join(", ") || "N/A",
      "Data Source": scanningcenter.dataCollection?.dataSource || "N/A",
    }));

    const facilityLayout = scanningcenters.map((scanningcenter, index) => ({
      "S.No": index + 1,
      "Center Name": scanningcenter.centerInfo?.centerName || "N/A",
      "Total MRI Machines":
        scanningcenter.facilityLayout?.totalMRImachines || "N/A",
      "Total CT Scanners":
        scanningcenter.facilityLayout?.totalCTscanners || "N/A",
      "Total X-Ray Machines":
        scanningcenter.facilityLayout?.totalXRaymachines || "N/A",
      "Total Ultrasound Rooms":
        scanningcenter.facilityLayout?.totalUltrasoundRooms || "N/A",
      "Avg. Daily Patient Footfall":
        scanningcenter.facilityLayout?.avgDailyPatientFootfall || "N/A",
      "Avg. Daily Staff Footfall":
        scanningcenter.facilityLayout?.avgDailyStaffFootfall || "N/A",
      "Waiting Area Size (sq. ft.)":
        scanningcenter.facilityLayout?.waitingAreaSize || "N/A",
      "Number of Changing Rooms":
        scanningcenter.facilityLayout?.numChangingRooms || "N/A",
      "Number of Patient Beds":
        scanningcenter.facilityLayout?.numPatientBeds || "N/A",
    }));

    const cleaninganddisinfection = scanningcenters.map(
      (scanningcenter, index) => ({
        "S.No": index + 1,
        "Center Name": scanningcenter.centerInfo?.centerName || "N/A",
        "Total Budget":
          scanningcenter.cleaningAndDisinfect?.totalBudget || "N/A",
        "Cost for Scanning Rooms (%)":
          scanningcenter.cleaningAndDisinfect?.costScanningRooms || "N/A",
        "Cost for Waiting Areas (%)":
          scanningcenter.cleaningAndDisinfect?.costWaitingAreas || "N/A",
        "Cost for Changing Rooms (%)":
          scanningcenter.cleaningAndDisinfect?.costChangingRooms || "N/A",
        "Cost for Equipment (%)":
          scanningcenter.cleaningAndDisinfect?.costEquipment || "N/A",
        "Current Methods":
          scanningcenter.cleaningAndDisinfect?.currentMethods?.join(", ") ||
          "N/A",
        "Frequency per Day":
          scanningcenter.cleaningAndDisinfect?.frequencyPerDay || "N/A",
        "Manual Disinfection Time (min)":
          scanningcenter.cleaningAndDisinfect?.manualDisinfectionTime || "N/A",
        Challenges:
          scanningcenter.cleaningAndDisinfect?.challenges?.join(", ") || "N/A",
      })
    );

    const healthandsafety = scanningcenters.map((scanningcenter, index) => ({
      "S.No": index + 1,
      "Center Name": scanningcenter.centerInfo?.centerName || "N/A",
      "Regulatory Standards":
        scanningcenter.healthAndSafety?.regulatoryStandards?.join(", ") ||
        "N/A",
      "Audit Frequency":
        scanningcenter.healthAndSafety?.auditFrequency?.join(", ") || "N/A",
      "Disinfection Complaints": scanningcenter.healthAndSafety
        ?.disinfectionComplaints?.hasComplaints
        ? "Yes"
        : "No",
      "Complaint Details":
        scanningcenter.healthAndSafety?.disinfectionComplaints?.details?.join(
          ", "
        ) || "N/A",
    }));

    const staffAndAutomationData = scanningcenters.map(
      (scanningcenter, index) => ({
        "S.No": index + 1,
        "Center Name": scanningcenter.centerInfo?.centerName || "N/A",
        "Number of Housekeeping Staff":
          scanningcenter.staffAndAutomation?.numHousekeepingStaff || "N/A",
        "Cleaning Shift Hours":
          scanningcenter.staffAndAutomation?.cleaningShiftHours || "N/A",
        "PPE Usage": scanningcenter.staffAndAutomation?.ppeUsage ? "Yes" : "No",
        "Automation Readiness": scanningcenter.staffAndAutomation
          ?.automationReadiness
          ? "Yes"
          : "No",
        "UV Concerns": scanningcenter.staffAndAutomation?.uvConcerns
          ? "Yes"
          : "No",
      })
    );

    const timeAndCostAnalysisData = scanningcenters.map(
      (scanningcenter, index) => ({
        "S.No": index + 1,
        "Center Name": scanningcenter.centerInfo?.centerName || "N/A",
        "Manual Disinfection Time Per Day (hrs)":
          scanningcenter.timeAndCostAnalysis?.manualDisinfectionTimePerDay ||
          "N/A",
        "Consumable Cost Per Month":
          scanningcenter.timeAndCostAnalysis?.consumableCostPerMonth || "N/A",
        "Labor Cost Per Month":
          scanningcenter.timeAndCostAnalysis?.laborCostPerMonth || "N/A",
        "Guest Refund Cost":
          scanningcenter.timeAndCostAnalysis?.guestRefundCost || "N/A",
        "Electricity Consumption":
          scanningcenter.timeAndCostAnalysis?.electricityConsumption || "N/A",
      })
    );

    const integrationNeedsData = scanningcenters.map(
      (scanningcenter, index) => ({
        "S.No": index + 1,
        "Center Name": scanningcenter.centerInfo?.centerName || "N/A",
        "Existing IT System": scanningcenter.integrationNeeds?.existingITSystem
          ? "Yes"
          : "No",
        "Integration Needs":
          scanningcenter.integrationNeeds?.integrationNeeds || "N/A",
        "Financial Model":
          scanningcenter.integrationNeeds?.financialModel || "N/A",
        "Customization Needs":
          scanningcenter.integrationNeeds?.customizationNeeds?.join(", ") ||
          "N/A",
      })
    );

    const demoAndNextStepsData = scanningcenters.map(
      (scanningcenter, index) => ({
        "S.No": index + 1,
        "Center Name": scanningcenter.centerInfo?.centerName || "N/A",
        "On-Site Demo Interest": scanningcenter.demoAndNextSteps
          ?.onSiteDemoInterest
          ? "Yes"
          : "No",
        "Pilot Project Readiness": scanningcenter.demoAndNextSteps
          ?.pilotProjectReadiness
          ? "Yes"
          : "No",
        "Key Decision Maker":
          scanningcenter.demoAndNextSteps?.keyDecisionMaker || "N/A",
        "Expected Timeline":
          scanningcenter.demoAndNextSteps?.expectedTimeline?.join(", ") ||
          "N/A",
      })
    );

    const additionalNotesData = scanningcenters.map(
      (scanningcenter, index) => ({
        "S.No": index + 1,
        "Center Name": scanningcenter.basicHotelInfo?.centerName || "N/A",
        "Additional Notes": scanningcenter.additionalnotes?.note || "N/A",
      })
    );
    const wb = XLSX.utils.book_new();

    // Convert JSON to Worksheet
    const ws1 = XLSX.utils.json_to_sheet(centerSheet);
    const ws2 = XLSX.utils.json_to_sheet(dataSheet);
    const ws3 = XLSX.utils.json_to_sheet(facilityLayout);
    const ws4 = XLSX.utils.json_to_sheet(cleaninganddisinfection);
    const ws5 = XLSX.utils.json_to_sheet(healthandsafety);
    const ws6 = XLSX.utils.json_to_sheet(staffAndAutomationData);
    const ws7 = XLSX.utils.json_to_sheet(timeAndCostAnalysisData);
    const ws8 = XLSX.utils.json_to_sheet(integrationNeedsData);
    const ws9 = XLSX.utils.json_to_sheet(demoAndNextStepsData);
    const ws10 = XLSX.utils.json_to_sheet(additionalNotesData);

    // Auto-size columns based on content
    ws1["!cols"] = autoFitColumns(centerSheet);
    ws2["!cols"] = autoFitColumns(dataSheet);
    ws3["!cols"] = autoFitColumns(facilityLayout);
    ws4["!cols"] = autoFitColumns(cleaninganddisinfection);
    ws5["!cols"] = autoFitColumns(healthandsafety);
    ws6["!cols"] = autoFitColumns(staffAndAutomationData);
    ws7["!cols"] = autoFitColumns(timeAndCostAnalysisData);
    ws8["!cols"] = autoFitColumns(integrationNeedsData);
    ws9["!cols"] = autoFitColumns(demoAndNextStepsData);
    ws10["!cols"] = autoFitColumns(additionalNotesData);

    // Set row height for better readability
    ws1["!rows"] = [{ hpx: 25 }];
    ws2["!rows"] = [{ hpx: 25 }];
    ws3["!rows"] = [{ hpx: 25 }];
    ws4["!rows"] = [{ hpx: 25 }];
    ws5["!rows"] = [{ hpx: 25 }];
    ws6["!rows"] = [{ hpx: 25 }];
    ws7["!rows"] = [{ hpx: 25 }];
    ws8["!rows"] = [{ hpx: 25 }];
    ws9["!rows"] = [{ hpx: 25 }];
    ws10["!rows"] = [{ hpx: 25 }];

    // Append sheets to workbook
    XLSX.utils.book_append_sheet(wb, ws1, "Center Info");
    XLSX.utils.book_append_sheet(wb, ws2, "Data Collection");
    XLSX.utils.book_append_sheet(wb, ws3, "Facility Layout");
    XLSX.utils.book_append_sheet(wb, ws4, "Cleaning and Disinfection");
    XLSX.utils.book_append_sheet(wb, ws5, "Health and Safety");
    XLSX.utils.book_append_sheet(wb, ws6, "Staff and Automation");
    XLSX.utils.book_append_sheet(wb, ws7, "Time and Cost Analysis");
    XLSX.utils.book_append_sheet(wb, ws8, "Integration Needs");
    XLSX.utils.book_append_sheet(wb, ws9, "Demo and Next Steps");
    XLSX.utils.book_append_sheet(wb, ws10, "Additional Notes");

    // Get the current date
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0"); // Months are zero-based
    const day = String(currentDate.getDate()).padStart(2, "0");

    // Format the date as dd-mm-yyyy
    const formattedDate = `${day}-${month}-${year}`;

    // Create the filename with the current date
    const filename = `ScanningCenterData${formattedDate}.xlsx`;

    // Write and download Excel file
    XLSX.writeFile(wb, filename);
  };

  const pageStyle = {
    position: "relative",
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    padding: "24px 32px 48px",
    boxSizing: "border-box",
  };

  if (loading) {
    return (
      <div
        style={{
          ...pageStyle,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
        }}
      >
        <span style={{ color: "#64748b", fontSize: 15, fontWeight: 500 }}>
          Loading...
        </span>
      </div>
    );
  }
  return (
    <div style={pageStyle}>
      <IconButton
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: 24,
          left: 24,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          color: "#4a4a4a",
          zIndex: 10,
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      <div style={{ textAlign: "center", marginTop: 20, marginBottom: 28 }}>
        <h1
          style={{
            margin: 0,
            marginBottom: 8,
            fontSize: "1.85rem",
            fontWeight: 700,
            letterSpacing: "-0.5px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Scanning-Center Data
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: "0.95rem",
            color: "#64748b",
            fontWeight: 400,
          }}
        >
          View and download scanning center data
        </p>
      </div>

      <button style={downloadButton} onClick={handleDownloadExcel}>
        📥 Download Excel
      </button>

      {scanningcenters.length === 0 ? (
        <div style={noDataStyle}>
          <h1>No Data Available</h1>
        </div>
      ) : (
        <>
          <h2 style={heading}>Center Information</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Center Name</th>
                  <th style={thStyle}>Center Type</th>
                  <th style={thStyle}>Accreditations</th>
                  <th style={thStyle}>Total Floor Area (sq. ft.)</th>
                  <th style={thStyle}>Total Number of Rooms</th>
                </tr>
              </thead>
              <tbody>
                {scanningcenters.map((scanningcenter, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {scanningcenter.centerInfo.centerName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.centerInfo.centerType || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.centerInfo.accreditations?.join(", ") ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.centerInfo.totalFloorArea || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.centerInfo.totalNumberOfRooms || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Data Collection</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Center Name</th>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Collected By</th>
                  <th style={thStyle}>Data Source</th>
                </tr>
              </thead>
              <tbody>
                {scanningcenters.map((scanningcenter, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {scanningcenter.centerInfo.centerName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.dataCollection.date
                        ? new Date(
                            scanningcenter.dataCollection.date
                          ).toLocaleDateString("en-GB")
                        : "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.dataCollection.collectedBy?.join(", ") ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.dataCollection.dataSource || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Facility Layout</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Center Name</th>
                  <th style={thStyle}>Total MRI Machines</th>
                  <th style={thStyle}>Total CT Scanners</th>
                  <th style={thStyle}>Total X-Ray Machines</th>
                  <th style={thStyle}>Total Ultrasound Rooms</th>
                  <th style={thStyle}>Avg. Daily Patient Footfall</th>
                  <th style={thStyle}>Avg. Daily Staff Footfall</th>
                  <th style={thStyle}>Waiting Area Size (sq. ft.)</th>
                  <th style={thStyle}>Number of Changing Rooms</th>
                  <th style={thStyle}>Number of Patient Beds</th>
                </tr>
              </thead>
              <tbody>
                {scanningcenters.map((scanningcenter, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {scanningcenter.centerInfo.centerName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.facilityLayout.totalMRImachines || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.facilityLayout.totalCTscanners || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.facilityLayout.totalXRaymachines || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.facilityLayout.totalUltrasoundRooms ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.facilityLayout.avgDailyPatientFootfall ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.facilityLayout.avgDailyStaffFootfall ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.facilityLayout.waitingAreaSize || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.facilityLayout.numChangingRooms || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.facilityLayout.numPatientBeds || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Cleaning and Disinfection</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Total Budget</th>
                  <th style={thStyle}>Center Name</th>
                  <th style={thStyle}>Cost for Scanning Rooms (%)</th>
                  <th style={thStyle}>Cost for Waiting Areas (%)</th>
                  <th style={thStyle}>Cost for Changing Rooms (%)</th>
                  <th style={thStyle}>Cost for Equipment (%)</th>
                  <th style={thStyle}>Current Methods</th>
                  <th style={thStyle}>Frequency per Day</th>
                  <th style={thStyle}>Manual Disinfection Time (min)</th>
                  <th style={thStyle}>Challenges</th>
                </tr>
              </thead>
              <tbody>
                {scanningcenters.map((scanningcenter, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {scanningcenter.centerInfo.centerName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.cleaningAndDisinfect.totalBudget || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.cleaningAndDisinfect.costScanningRooms ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.cleaningAndDisinfect.costWaitingAreas ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.cleaningAndDisinfect.costChangingRooms ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.cleaningAndDisinfect.costEquipment ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.cleaningAndDisinfect.currentMethods?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.cleaningAndDisinfect.frequencyPerDay ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.cleaningAndDisinfect
                        .manualDisinfectionTime || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.cleaningAndDisinfect.challenges?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Health and Safety</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Center Name</th>
                  <th style={thStyle}>Regulatory Standards</th>
                  <th style={thStyle}>Audit Frequency</th>
                  <th style={thStyle}>Disinfection Complaints</th>
                  <th style={thStyle}>Complaint Details</th>
                </tr>
              </thead>
              <tbody>
                {scanningcenters.map((scanningcenter, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {scanningcenter.centerInfo.centerName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.healthAndSafety.regulatoryStandards?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.healthAndSafety.auditFrequency?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.healthAndSafety.disinfectionComplaints
                        ?.hasComplaints
                        ? "Yes"
                        : "No"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.healthAndSafety.disinfectionComplaints?.details?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Staff and Automation</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Center Name</th>
                  <th style={thStyle}>Number of Housekeeping Staff</th>
                  <th style={thStyle}>Cleaning Shift Hours</th>
                  <th style={thStyle}>PPE Usage</th>
                  <th style={thStyle}>Automation Readiness</th>
                  <th style={thStyle}>UV Concerns</th>
                </tr>
              </thead>
              <tbody>
                {scanningcenters.map((scanningcenter, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {scanningcenter.centerInfo.centerName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.staffAndAutomation.numHousekeepingStaff ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.staffAndAutomation.cleaningShiftHours ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.staffAndAutomation.ppeUsage
                        ? "Yes"
                        : "No"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.staffAndAutomation.automationReadiness
                        ? "Yes"
                        : "No"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.staffAndAutomation.uvConcerns
                        ? "Yes"
                        : "No"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Time and Cost Analysis</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Center Name</th>
                  <th style={thStyle}>
                    Manual Disinfection Time Per Day (hrs)
                  </th>
                  <th style={thStyle}>Consumable Cost Per Month</th>
                  <th style={thStyle}>Labor Cost Per Month</th>
                  <th style={thStyle}>Guest Refund Cost</th>
                  <th style={thStyle}>Electricity Consumption</th>
                </tr>
              </thead>
              <tbody>
                {scanningcenters.map((scanningcenter, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {scanningcenter.centerInfo.centerName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.timeAndCostAnalysis
                        .manualDisinfectionTimePerDay || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.timeAndCostAnalysis
                        .consumableCostPerMonth || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.timeAndCostAnalysis.laborCostPerMonth ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.timeAndCostAnalysis.guestRefundCost ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.timeAndCostAnalysis
                        .electricityConsumption || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Integration Needs</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Center Name</th>
                  <th style={thStyle}>Existing IT System</th>
                  <th style={thStyle}>Integration Needs</th>
                  <th style={thStyle}>Financial Model</th>
                  <th style={thStyle}>Customization Needs</th>
                </tr>
              </thead>
              <tbody>
                {scanningcenters.map((scanningcenter, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {scanningcenter.centerInfo.centerName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.integrationNeeds.existingITSystem
                        ? "Yes"
                        : "No"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.integrationNeeds.integrationNeeds ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.integrationNeeds.financialModel || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.integrationNeeds.customizationNeeds?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Demo and Next Steps</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Center Name</th>
                  <th style={thStyle}>On-Site Demo Interest</th>
                  <th style={thStyle}>Pilot Project Readiness</th>
                  <th style={thStyle}>Key Decision Maker</th>
                  <th style={thStyle}>Expected Timeline</th>
                </tr>
              </thead>
              <tbody>
                {scanningcenters.map((scanningcenter, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {scanningcenter.centerInfo.centerName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.demoAndNextSteps.onSiteDemoInterest
                        ? "Yes"
                        : "No"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.demoAndNextSteps.pilotProjectReadiness
                        ? "Yes"
                        : "No"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.demoAndNextSteps.keyDecisionMaker ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.demoAndNextSteps.expectedTimeline?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>* Additional Notes *</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Center Name</th>
                  <th style={thStyle}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {scanningcenters.map((scanningcenter, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {scanningcenter.centerInfo.centerName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {scanningcenter.additionalnotes.note || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

// Internal CSS styles
const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  marginTop: "10px",
};

const thStyle = {
  backgroundColor: "#007bff",
  color: "#fff",
  padding: "10px",
  textAlign: "center",
  border: "1px solid #ddd",
};
const tableContainerStyle = {
  width: "100%",
  overflowX: "auto", // Enables horizontal scrolling
};
const tdStyle = {
  padding: "10px",
  border: "1px solid #ddd",
  textAlign: "center",
};

const heading = {
  fontSize: "24px",
  color: "black",
};

const downloadButton = {
  position: "absolute",
  top: "20px",
  right: "10px",
  backgroundColor: "#007bff",
  color: "white",
  border: "none",
  padding: "10px 15px",
  cursor: "pointer",
  borderRadius: "5px",
  fontSize: "14px",
};
const noDataStyle = {
  textAlign: "center",
  marginTop: "20px",
  fontSize: "20px",
  color: "#888",
};
export default ScanningcenterDetails;
